import { useEffect, useState } from 'react';
import { KeyRound, Plus, Loader2, Copy, Check, Trash2, ExternalLink, AlertTriangle, ShieldAlert, Webhook } from 'lucide-react';
import { listApiKeys, createApiKey, revokeApiKey, setApiKeyZusatzScopes, SCOPE_SENSIBEL, type ApiKeyDto } from '../lib/publicApiApi';
import ConfirmDialog from './admin/ConfirmDialog';
import ApiKeyWebhooks from './ApiKeyWebhooks';

interface Props {
  darkMode: boolean;
  cardClasses: string;
  inputClasses: string;
}

const datum = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

/**
 * Verwaltung der Maschinenzugänge. Der Klartext eines Schlüssels erscheint genau
 * einmal — direkt nach dem Anlegen; danach steht in der Datenbank nur ein Hash.
 * Deshalb hier ein eigener, auffälliger Kasten mit Kopieren-Knopf statt einer
 * beiläufigen Zeile in der Liste.
 */
export default function ApiSettings({ darkMode, cardClasses, inputClasses }: Props) {
  const [keys, setKeys] = useState<ApiKeyDto[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState('');

  const [name, setName] = useState('');
  const [scope, setScope] = useState<'read' | 'write'>('read');
  const [sensibel, setSensibel] = useState(false);
  const [sensibelFreigeben, setSensibelFreigeben] = useState<ApiKeyDto | null>(null);
  const [webhooksOffen, setWebhooksOffen] = useState<number | null>(null);
  const [legtAn, setLegtAn] = useState(false);
  const [neuerSchluessel, setNeuerSchluessel] = useState<{ name: string; key: string } | null>(null);
  const [kopiert, setKopiert] = useState(false);
  const [widerrufen, setWiderrufen] = useState<ApiKeyDto | null>(null);

  const laden = () => {
    setLaedt(true);
    listApiKeys()
      .then(setKeys)
      .catch((e: any) => setFehler(e?.message || 'Schlüssel konnten nicht geladen werden'))
      .finally(() => setLaedt(false));
  };
  useEffect(laden, []);

  const anlegen = async () => {
    if (!name.trim()) return;
    setLegtAn(true); setFehler('');
    try {
      const res = await createApiKey(name.trim(), scope, sensibel ? [SCOPE_SENSIBEL] : []);
      setNeuerSchluessel({ name: res.name, key: res.key });
      setName('');
      setSensibel(false);
      laden();
    } catch (e: any) {
      setFehler(e?.message || 'Anlegen fehlgeschlagen');
    } finally {
      setLegtAn(false);
    }
  };

  const sensibelSetzen = async (k: ApiKeyDto, an: boolean) => {
    setFehler('');
    try {
      const rest = (k.zusatzScopes ?? []).filter(s => s !== SCOPE_SENSIBEL);
      await setApiKeyZusatzScopes(k.id, an ? [...rest, SCOPE_SENSIBEL] : rest);
      laden();
    } catch (e: any) {
      setFehler(e?.message || 'Ändern fehlgeschlagen');
    }
  };

  const kopieren = async () => {
    if (!neuerSchluessel) return;
    try {
      await navigator.clipboard.writeText(neuerSchluessel.key);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2000);
    } catch { /* Zwischenablage nicht verfügbar → Text lässt sich markieren */ }
  };

  const label = darkMode ? 'text-slate-300' : 'text-slate-700';
  const leise = darkMode ? 'text-slate-400' : 'text-slate-500';

  return (
    <div className="space-y-6">
      <div className={cardClasses}>
        <div className="flex items-start gap-3 mb-4">
          <KeyRound className={`w-5 h-5 mt-0.5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
          <div className="min-w-0">
            <h3 className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>API-Schlüssel</h3>
            <p className={`text-xs mt-1 ${leise}`}>
              Für Anbindungen anderer Systeme. Ein Schlüssel gehört dem Team, nicht einer Person —
              er bleibt gültig, wenn jemand das Unternehmen verlässt. Der Mandant ergibt sich aus
              dem Schlüssel; fremde Daten sind damit nicht erreichbar.
            </p>
            <a
              href="/api/public/docs"
              target="_blank"
              rel="noopener"
              className={`inline-flex items-center gap-1 mt-2 text-xs font-medium ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
            >
              <ExternalLink className="w-3 h-3" /> Zur API-Dokumentation
            </a>
          </div>
        </div>

        {/* Einmalige Klartext-Anzeige */}
        {neuerSchluessel && (
          <div className={`mb-4 rounded-lg border p-4 ${darkMode ? 'border-amber-800 bg-amber-900/20' : 'border-amber-300 bg-amber-50'}`}>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
              <span className={`text-xs font-bold ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                „{neuerSchluessel.name}" — jetzt kopieren
              </span>
            </div>
            <p className={`text-xs mb-3 ${darkMode ? 'text-amber-300/80' : 'text-amber-800/80'}`}>
              Dieser Schlüssel wird nur jetzt angezeigt. Gespeichert ist nur seine Prüfsumme; geht er
              verloren, lässt er sich nicht wiederherstellen — dann einen neuen ausstellen und diesen
              widerrufen.
            </p>
            <div className="flex items-center gap-2">
              <code className={`flex-1 min-w-0 px-2.5 py-2 rounded-md text-xs font-mono break-all ${darkMode ? 'bg-slate-900 text-slate-200' : 'bg-white text-slate-800 border border-slate-200'}`}>
                {neuerSchluessel.key}
              </code>
              <button
                onClick={kopieren}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold ${
                  kopiert ? 'bg-green-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {kopiert ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {kopiert ? 'Kopiert' : 'Kopieren'}
              </button>
            </div>
            <button
              onClick={() => setNeuerSchluessel(null)}
              className={`mt-3 text-xs font-medium ${darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Habe ich kopiert — ausblenden
            </button>
          </div>
        )}

        {/* Anlegen */}
        <div className="flex flex-col sm:flex-row gap-2 mb-5">
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') anlegen(); }}
            placeholder={'Name, z. B. „Zapier" oder „Website-Formular"'}
            className={`${inputClasses} flex-1`}
          />
          <select value={scope} onChange={e => setScope(e.target.value as 'read' | 'write')} className={inputClasses}>
            <option value="read">Nur lesen</option>
            <option value="write">Lesen und schreiben</option>
          </select>
          <button
            onClick={anlegen}
            disabled={legtAn || !name.trim()}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
          >
            {legtAn ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Anlegen
          </button>
        </div>
        <label className={`flex items-start gap-2 -mt-3 mb-5 text-xs ${leise}`}>
          <input type="checkbox" checked={sensibel} onChange={e => setSensibel(e.target.checked)} className="mt-0.5" />
          <span>
            Steuer- und Bankdaten mitliefern (USt-IdNr., Steuernummer, Steuer-ID, Geburtsdatum, IBAN).
            Standard: aus. Jeder Abruf dieser Felder wird protokolliert.
          </span>
        </label>

        {fehler && <p className="text-xs text-red-600 mb-3">{fehler}</p>}

        {/* Liste */}
        {laedt ? (
          <div className={`flex items-center gap-2 text-xs ${leise}`}><Loader2 className="w-3.5 h-3.5 animate-spin" /> Wird geladen…</div>
        ) : keys.length === 0 ? (
          <p className={`text-xs ${leise}`}>Noch kein Schlüssel ausgestellt.</p>
        ) : (
          <ul className="space-y-2">
            {keys.map(k => (
              <li
                key={k.id}
                className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 rounded-lg border ${
                  k.revokedAt
                    ? darkMode ? 'border-slate-700 bg-slate-800/40 opacity-60' : 'border-slate-200 bg-slate-50 opacity-70'
                    : darkMode ? 'border-slate-700' : 'border-slate-200'
                }`}
              >
                <span className={`text-xs font-semibold ${label}`}>{k.name}</span>
                <code className={`text-xs font-mono ${leise}`}>{k.prefix}…</code>
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                  k.scope === 'write'
                    ? darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'
                    : darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'
                }`}>
                  {k.scope === 'write' ? 'lesen und schreiben' : 'nur lesen'}
                </span>
                {(k.zusatzScopes ?? []).includes(SCOPE_SENSIBEL) && (
                  <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full font-medium ${darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-800'}`}>
                    <ShieldAlert className="w-3 h-3" /> Steuer- und Bankdaten
                  </span>
                )}
                <span className={`text-xs ${leise}`}>angelegt {datum(k.createdAt)}</span>
                <span className={`text-xs ${leise}`}>
                  {k.lastUsedAt ? `zuletzt benutzt ${datum(k.lastUsedAt)}` : 'noch nie benutzt'}
                </span>
                <span className="ml-auto shrink-0">
                  {k.revokedAt ? (
                    <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      widerrufen {datum(k.revokedAt)}
                    </span>
                  ) : (
                    <>
                    <button
                      onClick={() => setWebhooksOffen(webhooksOffen === k.id ? null : k.id)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium ${darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}
                    >
                      <Webhook className="w-3.5 h-3.5" /> Webhooks
                    </button>
                    <button
                      onClick={() => (k.zusatzScopes ?? []).includes(SCOPE_SENSIBEL) ? sensibelSetzen(k, false) : setSensibelFreigeben(k)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium ${darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}
                    >
                      <ShieldAlert className="w-3.5 h-3.5" />
                      {(k.zusatzScopes ?? []).includes(SCOPE_SENSIBEL) ? 'Steuer-/Bankdaten entziehen' : 'Steuer-/Bankdaten freigeben'}
                    </button>
                    <button
                      onClick={() => setWiderrufen(k)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-slate-700"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Widerrufen
                    </button>
                    </>
                  )}
                </span>
                {webhooksOffen === k.id && !k.revokedAt && (
                  <ApiKeyWebhooks keyId={k.id} darkMode={darkMode} inputClasses={inputClasses} />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={!!widerrufen}
        title="Schlüssel widerrufen"
        message={`„${widerrufen?.name}" wird sofort ungültig; Anbindungen, die ihn benutzen, bekommen ab dann eine Fehlermeldung. Der Eintrag bleibt in der Liste stehen, damit nachvollziehbar ist, welcher Zugang wann abgeschaltet wurde.`}
        confirmLabel="Widerrufen"
        onConfirm={async () => {
          const k = widerrufen; setWiderrufen(null);
          if (!k) return;
          try { await revokeApiKey(k.id); laden(); }
          catch (e: any) { setFehler(e?.message || 'Widerrufen fehlgeschlagen'); }
        }}
        onCancel={() => setWiderrufen(null)}
      />

      <ConfirmDialog
        open={!!sensibelFreigeben}
        title="Steuer- und Bankdaten freigeben"
        message={`„${sensibelFreigeben?.name}" liefert ab sofort USt-IdNr., Steuernummer, Steuer-ID, Geburtsdatum und IBAN der Kunden mit aus. Jeder Abruf wird protokolliert. Nur freigeben, wenn das angebundene System diese Daten wirklich braucht.`}
        confirmLabel="Freigeben"
        onConfirm={async () => {
          const k = sensibelFreigeben; setSensibelFreigeben(null);
          if (k) await sensibelSetzen(k, true);
        }}
        onCancel={() => setSensibelFreigeben(null)}
      />
    </div>
  );
}
